import React, { useEffect, useRef, useState } from 'react';
import supabase from '../supabase';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

// Client-side ID-token sign-in instead of supabase.auth.signInWithOAuth(),
// which redirects through GoTrue and shows the raw *.supabase.co domain on
// Google's consent screen (rebranding that requires the paid Custom Domains
// add-on). This flow completes entirely on our own page.
const generateNonce = async () => {
  const nonce = btoa(String.fromCharCode(...crypto.getRandomValues(new Uint8Array(32))));
  const hashBuffer = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(nonce));
  const hashedNonce = Array.from(new Uint8Array(hashBuffer)).map(b => b.toString(16).padStart(2, '0')).join('');
  return [nonce, hashedNonce];
};

function loadGsiScript() {
  if (window.google?.accounts?.id) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const existing = document.getElementById('gsi-client-script');
    if (existing) { existing.addEventListener('load', () => resolve()); existing.addEventListener('error', reject); return; }
    const s = document.createElement('script');
    s.id = 'gsi-client-script';
    s.src = 'https://accounts.google.com/gsi/client';
    s.async = true; s.defer = true;
    s.onload = () => resolve();
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

const GSI_LOCALE = { 'zh-HK': 'zh-HK', 'zh-CN': 'zh-CN', 'en': 'en', 'ja': 'ja', 'ko': 'ko' };

export const GoogleSignInButton = ({ onLogin, onError, lang = 'zh-HK' }) => {
  const btnRef = useRef(null);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!GOOGLE_CLIENT_ID) { setLoadFailed(true); return; }
    (async () => {
      try {
        await loadGsiScript();
        const [nonce, hashedNonce] = await generateNonce();
        if (cancelled || !btnRef.current) return;
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          nonce: hashedNonce,
          use_fedcm_for_prompt: true,
          callback: async (response) => {
            const { error } = await supabase.auth.signInWithIdToken({
              provider: 'google', token: response.credential, nonce,
            });
            if (error) onError?.(error.message);
            else onLogin?.();
          },
        });
        window.google.accounts.id.renderButton(btnRef.current, {
          type: 'standard', theme: 'outline', size: 'large', shape: 'pill',
          text: 'continue_with', logo_alignment: 'center', width: 320,
          locale: GSI_LOCALE[lang] || 'en',
        });
      } catch {
        if (!cancelled) setLoadFailed(true);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loadFailed) return null; // caller falls back to email login only
  return <div ref={btnRef} style={{ display: 'flex', justifyContent: 'center', width: '100%', minHeight: 40 }} />;
};
